import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isAuthenticated } from '@/lib/api/auth';
import { useAuth } from '@/lib/useAuth';

type UserRole = 'admin' | 'teacher' | 'student' | 'parent';

interface AuthGuardProps {
  children: ReactNode;
  allowedRoles?: Array<UserRole>;
  fallbackPath?: string;
}

export default function AuthGuard({
  children,
  allowedRoles,
  fallbackPath = '/login',
}: AuthGuardProps) {
  const location = useLocation();
  const { data: user, isLoading } = useCurrentUser();

  // If still loading user data, show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated()) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  // If roles are specified, check if user has allowed role
  if (allowedRoles && allowedRoles.length > 0) {
    const userRole = getUserRole() as UserRole | null;
    
    // If user role is not in allowed roles, redirect based on role
    if (userRole && !allowedRoles.includes(userRole)) {
      // Redirect to appropriate dashboard based on role
      if (userRole === 'teacher') {
        return <Navigate to="/teacher" replace />;
      }
      if (userRole === 'admin') {
        return <Navigate to="/admin" replace />;
      }
      if (userRole === 'parent') {
        return <Navigate to="/parent" replace />;
      }
        return <Navigate to="/student" replace />;
    }

    // If no role yet (shouldn't happen normally), redirect
    if (!userRole) {
      return <Navigate to={fallbackPath} state={{ from: location }} replace />;
    }
  }

  return <>{children}</>;
}

// Protected route wrapper component
interface ProtectedRouteProps {
  children: ReactNode;
  requireAuth?: boolean;
  allowedRoles?: Array<UserRole>;
}

export function ProtectedRoute({
  children,
  requireAuth = true,
  allowedRoles,
}: ProtectedRouteProps) {
  const { isLoading, isAuthenticated: authenticated, userRole } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (requireAuth && !authenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && allowedRoles.length > 0) {
    if (!userRole || !allowedRoles.includes(userRole)) {
      // Redirect to appropriate dashboard
      if (userRole === 'teacher') {
        return <Navigate to="/teacher" replace />;
      }
      if (userRole === 'admin') {
        return <Navigate to="/admin" replace />;
      }
      if (userRole === 'parent') {
        return <Navigate to="/parent" replace />;
      }
      if (userRole === 'student') {
      return <Navigate to="/student" replace />;
      }
      return <Navigate to="/login" replace />;
    }
  }

  return <>{children}</>;
}
